import { useRef, useEffect, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { GripVertical } from "lucide-react";
import { TabProgressIndicator } from "./TabProgressIndicator";
import { Badge } from "@/components/ui/others/Badges";
import { cn } from "@/lib/utils";
import type { TabType } from "./TabsWrapper";
import { tabLabelWithSearchHighlight } from "./tabSearchHighlight";

function SortableTab({
  tab,
  isActive,
  onClick,
  setRef,
  style: externalStyle,
  filterQuery,
  reorderDisabled,
}: {
  tab: TabType;
  isActive: boolean;
  onClick: () => void;
  setRef: (el: HTMLDivElement | null) => void;
  style?: object;
  filterQuery?: string;
  /** Tabs stay clickable but can no longer be dragged (layout locked). */
  reorderDisabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.value, disabled: !!reorderDisabled });

  const combinedStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  const countString = tab.count?.toString() || "";
  const isThreeDigits = countString.length >= 3;

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        setRef(el);
      }}
      style={combinedStyle}
      className={cn(
        "relative group flex h-full min-h-10 select-none items-stretch",
        isDragging && "rounded-lg bg-white shadow-md",
      )}
    >
      {/* Drag handle — visible on hover */}
      <div
        {...attributes}
        {...listeners}
        className="absolute -left-0.5 top-1/2 -translate-y-1/2 z-10 p-0.5 rounded cursor-grab active:cursor-grabbing text-[#c5c9c7] opacity-0 group-hover:opacity-100 hover:text-[#697571] transition-opacity duration-150"
      >
        <GripVertical size={12} />
      </div>

      <div
        className="relative flex h-full min-h-0 cursor-pointer items-center gap-[8px] px-3 leading-5"
        onClick={onClick}
        style={externalStyle}
      >
        <div
          className={cn(
            "flex h-8 items-center gap-2 rounded-[10px] px-3 transition-[color] duration-200",
          )}
        >
          {tab.iconCount && (
            <Badge
              variant="count"
              className={`h-5 flex items-center justify-center ${
                isThreeDigits ? "px-2" : "px-1.5 min-w-[20px]"
              }`}
            >
              {tab.iconCount}
            </Badge>
          )}
          {tab.Icon && (
            <span
              className={`size-5 inline-flex shrink-0 [&_svg]:size-5 [&_svg]:shrink-0 ${
                isActive
                  ? "text-[var(--color-black-9)]"
                  : "text-[var(--color-black-7)]"
              }`}
            >
              <tab.Icon />
            </span>
          )}
          <span
            className={`inline-flex items-center text-b2-mid whitespace-nowrap [line-height:1.25rem] ${
              isActive
                ? "!text-[var(--color-black-9)]"
                : "!text-[var(--color-black-7)]"
            }`}
          >
            {filterQuery
              ? tabLabelWithSearchHighlight(tab.label ?? "", filterQuery)
              : tab.label}
          </span>
          {tab.count !== undefined && (
            <Badge
              variant="count"
              className={`h-5 flex items-center justify-center ${
                isThreeDigits ? "px-2" : "px-1.5 min-w-[20px]"
              }`}
            >
              {tab.count}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DraggableTabsWrapper({
  tabs,
  activeTab,
  setActiveTab,
  onReorder,
  className,
  style,
  indicatorClassName,
  filterQuery,
  reorderDisabled,
}: {
  tabs: TabType[];
  activeTab: TabType;
  setActiveTab: (tab: string) => void;
  onReorder?: (newOrder: string[]) => void;
  className?: string;
  style?: object;
  indicatorClassName?: string;
  /** Optional substring highlight (lead drawer tab search). */
  filterQuery?: string;
  /** "Globalise configs": the strip's order is the admin's — no dragging. */
  reorderDisabled?: boolean;
}) {
  const tabsRef = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const updateIndicatorPosition = useCallback(() => {
    const activeIndex = tabs.findIndex(
      (tab) => tab?.value === activeTab?.value
    );
    const activeTabElement = tabsRef.current[activeIndex];
    const container = containerRef.current;

    if (activeTabElement && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTabElement.getBoundingClientRect();

      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    } else {
      setIndicatorStyle({ left: 0, width: 0 });
    }
  }, [activeTab, tabs, filterQuery]);

  useEffect(() => {
    const activeIndex = tabs.findIndex(
      (tab) => tab?.value === activeTab?.value
    );
    const activeTabElement = tabsRef.current[activeIndex];
    if (activeTabElement) {
      activeTabElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
      requestAnimationFrame(() => updateIndicatorPosition());
    } else {
      updateIndicatorPosition();
    }

    const handleResize = () => updateIndicatorPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateIndicatorPosition]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tabs.findIndex((t) => t.value === active.id);
    const newIndex = tabs.findIndex((t) => t.value === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(tabs, oldIndex, newIndex);
    onReorder?.(reordered.map((t) => t.value));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => updateIndicatorPosition());
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={tabs.map((t) => t.value)}
        strategy={horizontalListSortingStrategy}
      >
        <div
          ref={containerRef}
          className={cn("relative flex min-h-10 items-stretch", className)}
        >
          {tabs.map((tab, index) => (
            <SortableTab
              key={tab.value}
              tab={tab}
              isActive={activeTab?.value === tab.value}
              onClick={() => setActiveTab(tab.value)}
              setRef={(el) => {
                tabsRef.current[index] = el;
              }}
              style={style}
              filterQuery={filterQuery}
              reorderDisabled={reorderDisabled}
            />
          ))}
          <TabProgressIndicator
            className={indicatorClassName}
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
            }}
          />
        </div>
      </SortableContext>
    </DndContext>
  );
}
